import React from "react";
import { Helmet } from "react-helmet-async";
import "../styles/PrivacyPolicy.css";
import MainHeader from "../components/Main/MainHeader";
import HomeFooter from "../components/Home/HomeFooter";

export default function PrivacyPolicy() {
  return (
    <>
    <MainHeader />
    <div className="privacypolicy">
      {/* SEO Meta */}
      <Helmet>
        <title>Privacy Policy | Anyworq – A to Z Services in Bangalore</title>
        <meta
          name="description"
          content="Read Anyworq’s Privacy Policy to understand how we collect, use, and protect your personal information. Trusted A to Z service platform in Bangalore."
        />
        <meta
          name="keywords"
          content="Privacy Policy Anyworq, Data Protection Bangalore, Service Platform Privacy, Anyworq data policy, A to Z services Bangalore"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.anyworq.com/privacypolicy" />

        {/* Open Graph Tags */}
        <meta
          property="og:title"
          content="Privacy Policy | Anyworq – A to Z Services in Bangalore"
        />
        <meta
          property="og:description"
          content="Understand how Anyworq protects your privacy and handles your data responsibly. Trusted service marketplace in Bangalore."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.anyworq.com/privacypolicy" />
        <meta
          property="og:image"
          content="https://www.anyworq.com/Favicon.png"
        />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Privacy Policy | Anyworq – A to Z Services in Bangalore"
        />
        <meta
          name="twitter:description"
          content="Learn how Anyworq collects and protects your personal data. Trusted A to Z services in Bangalore."
        />
        <meta
          name="twitter:image"
          content="https://www.anyworq.com/Favicon.png"
        />

        {/* Structured Data (Schema.org) */}
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Privacy Policy - Anyworq",
            "description": "Privacy Policy page explaining how Anyworq collects, uses, and safeguards user data.",
            "publisher": {
              "@type": "Organization",
              "name": "Anyworq",
              "url": "https://www.anyworq.com",
              "logo": "https://www.anyworq.com/Favicon.png"
            },
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What personal data does Anyworq collect?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "We collect your name, contact details, enquiry information, and usage data to improve services."
                }
              },
              {
                "@type": "Question",
                "name": "Do you share my personal information with third parties?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "We only share necessary data with verified dealers or trusted partners. We never sell your data."
                }
              },
              {
                "@type": "Question",
                "name": "Can I delete my data?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, you can request deletion of your personal data anytime by emailing support@anyworq.com."
                }
              }
            ]
          }
          `}
        </script>
      </Helmet>

      {/* Header */}
      <header className="privacypolicy-header">
        <h1>Privacy Policy – Anyworq</h1>
        <p>
          At Anyworq, we respect your privacy and are committed to protecting
          your personal information. This Privacy Policy explains how we
          collect, use, store, and safeguard your data when you use our
          platform.
        </p>
      </header>

      {/* Sections */}
      <section>
        <h2>1. Information We Collect</h2>
        <ul>
          <li>
            <strong>Personal Information:</strong> Name, phone number, email,
            address, and service enquiry details when you register or request a
            service.
          </li>
          <li>
            <strong>Dealer Information:</strong> Business name, contact details,
            and service category for dealer profiles.
          </li>
          <li>
            <strong>Usage Data:</strong> IP address, browser type, device
            information, and interactions on our website or platform.
          </li>
        </ul>
      </section>

      <section>
        <h2>2. How We Use Your Information</h2>
        <ul>
          <li>Provide, manage, and improve our services.</li>
          <li>Connect users with relevant dealers based on enquiries.</li>
          <li>
            Communicate updates, confirmations, and customer support
            information.
          </li>
          <li>Enhance platform security and prevent fraud.</li>
          <li>Comply with applicable legal requirements.</li>
        </ul>
      </section>

      <section>
        <h2>3. Sharing of Information</h2>
        <ul>
          <li>
            Verified dealers/service providers to handle your enquiries.
          </li>
          <li>
            Trusted third-party service providers (e.g., IT, payment processors,
            hosting providers) who support our operations.
          </li>
          <li>Authorities, when required by law.</li>
        </ul>
      </section>

      <section>
        <h2>4. Cookies & Tracking</h2>
        <ul>
          <li>Improve user experience.</li>
          <li>Analyze website traffic.</li>
          <li>Personalize content and services.</li>
        </ul>
        <p>
          You may disable cookies through your browser settings, but this may
          affect platform functionality.
        </p>
      </section>

      <section>
        <h2>5. Data Security</h2>
        <p>
          We implement technical and organizational measures to protect your
          data from unauthorized access, disclosure, or misuse.
        </p>
      </section>

      <section>
        <h2>6. Your Rights</h2>
        <ul>
          <li>Access, update, or delete your personal data.</li>
          <li>Opt-out of marketing communications.</li>
          <li>
            Contact us for any privacy-related requests or concerns.
          </li>
        </ul>
      </section>

      <section>
        <h2>7. Contact Us</h2>
        <p>
          For any privacy or data-related concerns, please contact us at: <br />
          📧 <a href="mailto:support@anyworq.com">support@anyworq.com</a>
        </p>
      </section>

      {/* FAQ */}
      <section className="privacypolicy-faq">
        <h2>Privacy Policy – FAQ</h2>
        <div className="faq-item">
          <h3> What personal data does Anyworq collect?</h3>
          <p>
            We collect basic information such as your name, contact details,
            enquiry details, and usage data to provide and improve our services.
          </p>
        </div>
        <div className="faq-item">
          <h3> Do you share my personal information with third parties?</h3>
          <p>
            No, we do not sell your data. We only share it with verified dealers
            (to fulfill your enquiries) or trusted partners who help us operate
            the platform.
          </p>
        </div>
        <div className="faq-item">
          <h3> How secure is my data on Anyworq?</h3>
          <p>
            We use strong technical and organizational security measures to
            protect your data from unauthorized access, misuse, or disclosure.
          </p>
        </div>
        <div className="faq-item">
          <h3> Can I delete my data?</h3>
          <p>
            Yes. You may contact us anytime to request deletion of your data,
            unless we are legally required to retain it.
          </p>
        </div>
        <div className="faq-item">
          <h3> Do you use cookies?</h3>
          <p>
            Yes, we use cookies to enhance your browsing experience and analyze
            website traffic. You can disable cookies in your browser settings if
            you wish.
          </p>
        </div>
      </section>
    </div>
    <HomeFooter />
    </>
  );
}
